import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TableVirtuoso } from 'react-virtuoso'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useListVatBanksQuery,
  useListVatInvoicesQuery,
  useSubmitVatMutation,
  type VatInvoiceItem,
} from '@/store/api/procurementApi'
import { useLookupVendorsQuery } from '@/store/api/lookupsApi'
import { API_BASE_URL } from '@/config/api'
import { getStoredToken } from '@/services/auth'
import { getErrorMessage } from '@/utils/error'
import { titleCaseName } from '@/utils/text'
import {
  base64ToBlob,
  errorDetail,
  filenameFromDisposition,
  isWindowsOS,
  openOutlookDraft,
  triggerDownload,
  XLSX_MIME,
} from '@/utils/outlookMail'
import type { LookupVendor, PayDataDnttParts } from '@/types/api'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  numSx,
  roSx,
  sectionHeaderSx,
} from '../purchase/dialogStyle'

interface Props {
  open: boolean
  cpCode: string
  supplierLabel: string
  paydataIds: number[]
  /** invoice_date của dòng ticked đầu tiên (Access btnVAT) → default date range = tháng đó. */
  invoiceDate?: string | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

/** Access `btnSave_Click` guard messages (verbatim copy → English). */
const PAID_DATE_REQUIRED = 'Please choose VAT Paid Date!'
const BANK_REQUIRED = 'Please choose VAT Paid Bank Account!'

/** Access Format = "* #,##0.00;..." → Standard 2dp display. */
const fmtAmount = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return ''
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Access Format = "d-mmm-yy" → DD-MMM-YYYY display. */
const fmtDate = (v: string | null | undefined): string =>
  v ? dayjs(v).format('DD-MMM-YYYY') : ''

const rowKey = (it: VatInvoiceItem): string => `${it.invoice_no ?? ''}|${it.contract_no ?? ''}`

// ---------------------------------------------------------------------------
// Datasheet columns (/convert-table) — thứ tự + caption theo ColumnOrder của
// subform Access `frmProcurementQryPayDataSendVAT`. `checked_row` → cột tick
// riêng (KHÔNG trong TABLE_COLUMNS). Format: date DD-MMM-YYYY, số Standard 2dp.
// ---------------------------------------------------------------------------
const TICK_COL_WIDTH = 56

type ColumnKind = 'text' | 'date' | 'int' | 'number'
type ColumnDef = {
  key: keyof VatInvoiceItem
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  proper?: boolean
  width?: number
}

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'invoice_id', label: 'ID', kind: 'int', align: 'right', width: 60 },
  { key: 'invoice_no', label: 'InvoiceNo', width: 130 },
  { key: 'contract_no', label: 'ContractNo', width: 100 },
  { key: 'invoice_date', label: 'Invoice Date', kind: 'date', width: 116 },
  { key: 'paid_vat_txt', label: 'VATPaidRequest-Status', filter: 'checkboxes', width: 150 },
  { key: 'paid_vat_value', label: 'VATPaidRequest-Amount', kind: 'number', align: 'right', width: 140 },
  { key: 'paid_vat_date', label: 'VATPaidRequest-Date', kind: 'date', width: 150 },
  { key: 'vat_amount', label: 'InvoiceVAT Amount', kind: 'number', align: 'right', width: 132 },
  { key: 'total_amount', label: 'Invoice Amount', kind: 'number', align: 'right', width: 140 },
  { key: 'total_amount_after_tax', label: 'Invoice Amount (afterVAT)', kind: 'number', align: 'right', width: 200 },
]

const cellValue = (row: VatInvoiceItem, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return fmtDate(val as string)
  if (col.kind === 'int') return String(Math.round(Number(val)))
  if (col.kind === 'number') return fmtAmount(Number(val))
  if (col.proper) return titleCaseName(String(val))
  return String(val)
}

/** Cột tiền có Total (Access TotalsRow=1, AggregateType=0). */
const SUM_KEYS = new Set<keyof VatInvoiceItem>([
  'paid_vat_value',
  'vat_amount',
  'total_amount',
  'total_amount_after_tax',
])

// Footer Total (chuẩn /convert-table §10.10b): bold, bg opaque, borderTop, sticky
// đáy để luôn thấy khi scroll. color text.primary để KHÔNG bị MUI footer làm xám.
const footerCellSx = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  py: 0.5,
  color: 'text.primary',
  bgcolor: 'background.paper',
  borderTop: '2px solid rgba(11, 54, 51, 0.24)',
  position: 'sticky' as const,
  bottom: 0,
  zIndex: 3,
}

/**
 * Pay Data → "Send - DNTT VAT" dialog — converts Access form
 * `frmProcurementQryPayDataSendVATAdd` (+ subform `frmProcurementQryPayDataSendVAT`).
 *
 * Flow (Access `btnVAT_Click` → dialog → `btnSave_Click` → `Send_DNTT_VAT`):
 *  1. Seed từ dòng ticked đầu tiên: cp_code + date range = tháng của invoice_date.
 *  2. Vendor (cp_code) là combobox CHỌN LẠI ĐƯỢC (Access `cp_code_Change/Click`):
 *     đổi cp_code → clear contract_no + refresh list + bank (default = item[0]).
 *  3. User ticks rows (Tick All), chọn VAT Paid Date (default hôm nay) + Bank.
 *  4. Send: `btnSave_Click` lưu khai VAT (soft-delete + insert invoice_vat_detail)
 *     RỒI `Send_DNTT_VAT` — tải Excel DNTT-VAT + mở Outlook gửi mail kèm file
 *     (Windows .eml / Mac mailto + kéo Excel), giống Send-DNTT.
 *
 * No fullScreen; mobile stacks the filter grid to one column and the list
 * scrolls horizontally inside its own container.
 */
export default function PayDataSendVatDialog({
  open,
  cpCode: cpCodeSeed,
  supplierLabel,
  paydataIds,
  invoiceDate,
  canEdit,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()

  // ----- Vendor (cp_code) — Access combobox chọn lại được -----
  const [cpCode, setCpCode] = useState<string>('')
  const [cpName, setCpName] = useState<string>('')
  const [vendorInput, setVendorInput] = useState<string>('')

  // ----- Filter state (Filter Area) -----
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [contractNo, setContractNo] = useState<string>('')

  // ----- Input Data state -----
  const [paidDate, setPaidDate] = useState<string>('')
  const [bankAccount, setBankAccount] = useState<string>('')

  // ----- Tick selection (checked_row) -----
  const [ticked, setTicked] = useState<Set<string>>(new Set())

  // Bước gửi mail DNTT-VAT sau khi Save (Send_DNTT_VAT) đang chạy.
  const [sendingMail, setSendingMail] = useState<boolean>(false)

  // ----- dataList (/convert-table): global Search + per-column ▽ + sort + resize -----
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof VatInvoiceItem> | null>(null)
  const { getWidth, startResize } = useColumnWidths('procurement.pay-data-vat.send-vat.grid')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof VatInvoiceItem
    position: ColumnFilterAnchorPosition
  } | null>(null)

  // Re-init mỗi lần mở dialog (Access btnVAT_Click seed). date range = tháng của
  // invoice_date dòng đầu (rỗng nếu rỗng); paid_date default = hôm nay (Now()).
  useEffect(() => {
    if (!open) return
    setCpCode(cpCodeSeed || '')
    setCpName(supplierLabel || '')
    setVendorInput('')
    if (invoiceDate && dayjs(invoiceDate).isValid()) {
      const d = dayjs(invoiceDate)
      setDateFrom(d.startOf('month').format('YYYY-MM-DD'))
      setDateTo(d.endOf('month').format('YYYY-MM-DD'))
    } else {
      setDateFrom('')
      setDateTo('')
    }
    setContractNo('')
    setPaidDate(dayjs().format('YYYY-MM-DD'))
    setBankAccount('')
    setTicked(new Set())
    setSearchTerm('')
    columnFilters.clearAll()
    setSortState(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cpCodeSeed, supplierLabel, invoiceDate])

  // Vendor autocomplete options (Access cp_code RowSource public_cp_vn).
  const { data: vendorList } = useLookupVendorsQuery(
    open ? { q: vendorInput || undefined } : undefined,
    { skip: !open },
  )
  const vendorOptions = useMemo(() => vendorList ?? [], [vendorList])

  // Vendor đang chọn (đảm bảo luôn nằm trong options để Autocomplete không cảnh báo).
  const selectedVendor = useMemo<LookupVendor | null>(() => {
    if (!cpCode) return null
    return vendorOptions.find((v) => v.code === cpCode) ?? { code: cpCode, name: cpName || cpCode }
  }, [cpCode, cpName, vendorOptions])
  const vendorAutoOptions = useMemo<LookupVendor[]>(() => {
    if (selectedVendor && !vendorOptions.some((v) => v.code === selectedVendor.code)) {
      return [selectedVendor, ...vendorOptions]
    }
    return vendorOptions
  }, [selectedVendor, vendorOptions])
  const vendorLabel = (o: LookupVendor): string => {
    const code = o.code ?? ''
    const name = o.name ?? ''
    return !name || name === code ? code : `${code} — ${name}`
  }

  // refresh_vat_paid — invoice list filtered by cp_code + contract_no + date range.
  // Chưa chọn Vendor (cp_code rỗng) → KHÔNG load (bảng rỗng), thay vì load all.
  const { data: invData, isFetching: invLoading } = useListVatInvoicesQuery(
    open && cpCode
      ? {
          cp_code: cpCode,
          contract_no: contractNo || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        }
      : { cp_code: '__none__' },
    { skip: !open || !cpCode, refetchOnMountOrArgChange: true },
  )
  const invoices = useMemo(() => invData?.items ?? [], [invData])

  // Bank lookup (QryCPBank by cp_code).
  const { data: bankData } = useListVatBanksQuery(open ? { cp_code: cpCode || undefined } : undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  })
  const banks = useMemo(() => bankData?.items ?? [], [bankData])

  // Access: when the list refreshes, default bank_account = first option.
  useEffect(() => {
    if (!open) return
    setBankAccount((prev) => {
      if (prev && banks.some((bk) => bk.bank_account === prev)) return prev
      return banks.length > 0 ? (banks[0].bank_account ?? '') : ''
    })
  }, [open, banks])

  // cp_code_Click: đổi vendor → clear contract_no (refresh list tự động qua query args).
  const handleCpCodeChange = (code: string, name: string) => {
    setCpCode(code)
    setCpName(name)
    setContractNo('')
  }

  // Distinct contract numbers from the list → contract_no Autocomplete options.
  const contractOptions = useMemo(() => {
    const set = new Set<string>()
    for (const it of invoices) if (it.contract_no) set.add(it.contract_no)
    return Array.from(set).sort()
  }, [invoices])

  const [submitVat, { isLoading: saving }] = useSubmitVatMutation()

  // Filter (global Search + per-column) → sort (/convert-table).
  const filteredRows = useMemo(
    () =>
      invoices.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [invoices, searchTerm, columnFilters.filters],
  )
  const displayRows = useMemo(
    () => sortTableRows(filteredRows, sortState, TABLE_COLUMNS),
    [filteredRows, sortState],
  )
  const toggleSort = (key: keyof VatInvoiceItem) =>
    setSortState((prev) => toggleTableSort(prev, key))

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    filterPopover.close()
  }

  // Per-column popover meta.
  const popoverColumn = useMemo(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, invoices) : 'text'),
    [popoverColumn, invoices],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(invoices, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, invoices],
  )

  // Tick (checked_row) keyed on rowKey (invoice_no|contract_no). Select-all phản
  // ánh CÁC dòng đang hiển thị (đã filter) — giống reference.
  const displayedKeys = useMemo(() => displayRows.map(rowKey), [displayRows])
  const allTicked = displayedKeys.length > 0 && displayedKeys.every((k) => ticked.has(k))
  const someTicked = displayedKeys.some((k) => ticked.has(k)) && !allTicked
  const toggleTickAll = () =>
    setTicked((prev) => {
      const next = new Set(prev)
      if (displayedKeys.every((k) => next.has(k))) displayedKeys.forEach((k) => next.delete(k))
      else displayedKeys.forEach((k) => next.add(k))
      return next
    })
  const toggleRow = (it: VatInvoiceItem) => {
    const key = rowKey(it)
    setTicked((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const selectedInvoices = useMemo(
    () => invoices.filter((it) => ticked.has(rowKey(it))),
    [invoices, ticked],
  )
  const minTableWidth = useMemo(
    () =>
      TICK_COL_WIDTH +
      TABLE_COLUMNS.reduce((s, c) => s + (getWidth(String(c.key)) ?? c.width ?? 120), 0),
    [getWidth],
  )

  // Dòng Total (Access TotalsRow=1, AggregateType=0) — sum ĐÚNG 4 cột tiền của
  // subform: total_amount, vat_amount, total_amount_after_tax, paid_vat_value.
  const totals = useMemo(() => {
    const acc = { total_amount: 0, vat_amount: 0, total_amount_after_tax: 0, paid_vat_value: 0 }
    for (const it of filteredRows) {
      acc.total_amount += it.total_amount ?? 0
      acc.vat_amount += it.vat_amount ?? 0
      acc.total_amount_after_tax += it.total_amount_after_tax ?? 0
      acc.paid_vat_value += it.paid_vat_value ?? 0
    }
    return acc
  }, [filteredRows])

  const paidDateMissing = !paidDate || paidDate.trim() === ''
  const bankMissing = !bankAccount || bankAccount.trim() === ''
  const busy = saving || sendingMail
  const canSave =
    canEdit && !busy && !paidDateMissing && !bankMissing && selectedInvoices.length > 0

  // Access `Send_DNTT_VAT` (chạy sau btnSave): tải Excel DNTT-VAT + mở Outlook
  // gửi mail kèm file (Windows .eml / Mac mailto + kéo Excel). Giống Send-DNTT.
  const runSendDnttVat = async (
    invoicesPayload: { invoice_no: string; contract_no: string; vat_amount: number | null }[],
  ) => {
    const auth = {
      Authorization: `Bearer ${getStoredToken() ?? ''}`,
      'Content-Type': 'application/json',
    }
    const body = JSON.stringify({ invoices: invoicesPayload })
    if (isWindowsOS()) {
      const resp = await fetch(`${API_BASE_URL}/purchase/pay-data-vat/mail/dntt-vat/eml`, {
        method: 'POST',
        headers: auth,
        body,
      })
      if (!resp.ok) {
        snack.error(await errorDetail(resp, 'Could not create DNTT VAT email.'))
        return
      }
      const blob = await resp.blob()
      const filename = filenameFromDisposition(
        resp.headers.get('Content-Disposition') ?? '',
        'DNTT_VAT.eml',
      )
      triggerDownload(blob, filename)
      snack.success('Downloaded .eml — double-click to open Outlook, review then click Send.')
    } else {
      const resp = await fetch(`${API_BASE_URL}/purchase/pay-data-vat/mail/dntt-vat/parts`, {
        method: 'POST',
        headers: auth,
        body,
      })
      if (!resp.ok) {
        snack.error(await errorDetail(resp, 'Could not create DNTT VAT email.'))
        return
      }
      const parts: PayDataDnttParts = await resp.json()
      if (parts.excel_base64) {
        triggerDownload(base64ToBlob(parts.excel_base64, XLSX_MIME), parts.excel_filename || 'DNTT_VAT.xlsx')
      }
      openOutlookDraft({ to: parts.to, cc: parts.cc, subject: parts.subject, body: parts.body })
      snack.info('Outlook mở sẵn thư — hãy KÉO file Excel vừa tải vào email rồi bấm Send.')
    }
  }

  const handleSend = async () => {
    if (!canEdit) return
    if (paidDateMissing) {
      snack.warning(PAID_DATE_REQUIRED)
      return
    }
    if (bankMissing) {
      snack.warning(BANK_REQUIRED)
      return
    }
    if (selectedInvoices.length === 0) {
      snack.warning('No records to choose!')
      return
    }
    // Snapshot invoices trước khi list bị invalidate sau submit.
    const invoicesPayload = selectedInvoices.map((it) => ({
      invoice_no: it.invoice_no ?? '',
      contract_no: it.contract_no ?? '',
      vat_amount: it.vat_amount ?? null,
    }))
    try {
      const res = await submitVat({
        paid_date: paidDate,
        bank_account: bankAccount,
        cp_code: cpCode || null,
        invoices: invoicesPayload,
      }).unwrap()
      snack.success(res.message || 'VAT declaration submitted')
      // btnSave_Click → Send_DNTT_VAT: gửi mail DNTT-VAT trước khi đóng.
      setSendingMail(true)
      try {
        await runSendDnttVat(invoicesPayload)
      } catch (mailErr) {
        snack.error(getErrorMessage(mailErr, 'Could not create DNTT VAT email.'))
      } finally {
        setSendingMail(false)
      }
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to submit VAT declaration'))
    }
  }

  const handleClose = () => {
    if (busy) return
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (busy) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { width: '96vw', maxWidth: 1700 } }}
    >
      <Box
        sx={{
          pb: 1,
          pt: 1.25,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          component="span"
          noWrap
          title={`Send VAT — ${cpName || cpCode}`}
          sx={{
            flex: '1 1 auto',
            fontWeight: 700,
            fontSize: '1rem',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Send VAT — {cpName || cpCode}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSend()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {busy ? <CircularProgress size={14} color="inherit" /> : 'Send'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={busy}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </Box>
      <Divider />

      <DialogContent dividers sx={compactDialogContentSx}>
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
            Read-only — you don&apos;t have edit permission for this action.
          </Alert>
        )}

        {/* ===== Filter Area ===== */}
        <Typography sx={{ ...sectionHeaderSx, mb: 1 }}>Filter Area</Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            alignItems: 'start',
          }}
        >
          {/* Row 1: InvoiceDate (From-To) — date_from | date_to */}
          <DatePicker
            label="Invoice Date (From)"
            format="DD-MMM-YYYY"
            value={dateFrom ? dayjs(dateFrom) : null}
            onChange={(v) => setDateFrom(v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputLabelProps: { shrink: true },
                sx: { gridColumn: { xs: 1, sm: 1 } },
              },
            }}
          />
          <DatePicker
            label="Invoice Date (To)"
            format="DD-MMM-YYYY"
            value={dateTo ? dayjs(dateTo) : null}
            onChange={(v) => setDateTo(v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputLabelProps: { shrink: true },
                sx: { gridColumn: { xs: 1, sm: 2 } },
              },
            }}
          />

          {/* Row 2: Vendor (cp_code — combobox chọn lại được, Access cp_code_Change/Click) | CP Name */}
          <Autocomplete
            size="small"
            options={vendorAutoOptions}
            value={selectedVendor}
            onChange={(_e, v) => handleCpCodeChange(v?.code ?? '', v?.name ?? '')}
            onInputChange={(_e, val, reason) => {
              if (reason === 'input') setVendorInput(val)
            }}
            // Pattern codeOnly (/convert-form): đã có ô CP Name kế bên hiện tên →
            // sau khi chọn ô combo CHỈ hiện code; dropdown VẪN hiện "code — name".
            getOptionLabel={(o) => (o ? String(o.code ?? '') : '')}
            renderOption={(liProps, o) => (
              <li {...liProps} key={o.code}>
                {vendorLabel(o)}
              </li>
            )}
            filterOptions={(opts) => opts}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            disabled={!canEdit || busy}
            sx={{ gridColumn: { xs: 1, sm: 1 } }}
            renderInput={(params) => (
              <TextField {...params} label="Vendor" InputLabelProps={{ shrink: true }} />
            )}
          />
          <TextField
            label="CP Name"
            size="small"
            fullWidth
            value={cpName ?? ''}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ ...roSx, gridColumn: { xs: 1, sm: 2 } }}
          />

          {/* Row 3: Contract No filter (Autocomplete from list) */}
          <Autocomplete
            size="small"
            options={contractOptions}
            value={contractNo || null}
            onChange={(_e, v) => setContractNo(v ?? '')}
            isOptionEqualToValue={(a, b) => a === b}
            sx={{ gridColumn: { xs: 1, sm: 1 } }}
            renderInput={(params) => (
              <TextField {...params} label="Contract No" InputLabelProps={{ shrink: true }} />
            )}
          />
        </Box>

        {/* ===== Input Data ===== */}
        <Typography sx={{ ...sectionHeaderSx, mt: 2, mb: 1 }}>Input Data</Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            alignItems: 'start',
          }}
        >
          {/* paid_date (required) */}
          <DatePicker
            label="VAT Paid Request - Date *"
            format="DD-MMM-YYYY"
            value={paidDate ? dayjs(paidDate) : null}
            onChange={(v) => setPaidDate(v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
            disabled={!canEdit}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                error: paidDateMissing && someTicked,
                InputLabelProps: { shrink: true },
                sx: { gridColumn: { xs: 1, sm: 1 } },
              },
            }}
          />

          {/* bank_account (required) — VAT Paid Request - Bank */}
          <Autocomplete
            size="small"
            options={banks}
            value={banks.find((bk) => bk.bank_account === bankAccount) ?? null}
            onChange={(_e, v) => setBankAccount(v?.bank_account ?? '')}
            getOptionLabel={(o) =>
              o ? (o.bank_info ? `${o.bank_account} — ${o.bank_info}` : (o.bank_account ?? '')) : ''
            }
            isOptionEqualToValue={(a, b) => a.bank_account === b.bank_account}
            disabled={!canEdit}
            sx={{ gridColumn: { xs: 1, sm: 2 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="VAT Paid Request - Bank *"
                error={bankMissing && someTicked}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Box>

        {/* ===== Invoice list (subForm frmProcurementQryPayDataSendVAT) — dataList
             template chuẩn (/convert-table): FilterBar global Search + per-column ▽
             + sort + resize + TableVirtuoso + app-dataframe. Cột tick "To be Sent"
             = multi-row select (Access checked_row). Footer Total = 4 cột tiền
             (Access TotalsRow=1, AggregateType=0). ===== */}
        <Typography sx={{ ...sectionHeaderSx, mt: 2, mb: 1 }}>Records to Send</Typography>
        <FilterBar
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
          rightSlot={
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              {selectedInvoices.length} SELECTED — only ticked rows are sent
            </Typography>
          }
        >
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
        </FilterBar>

        <Divider sx={{ mb: 0.5 }} />

        <Box className="app-dataframe" sx={{ height: { xs: 300, sm: 420 } }}>
          <Box
            className="app-dataframe-toolbar"
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}
          >
            <Typography variant="caption">Total: {filteredRows.length}</Typography>
            <Typography variant="caption">Loaded: {invoices.length}</Typography>
            {invLoading && <CircularProgress size={12} />}
          </Box>

          {/* Global Search popover */}
          <FilterPopover
            open={filterPopover.state !== null}
            anchorEl={filterPopover.state?.anchor ?? null}
            onClose={() => filterPopover.close()}
          >
            {filterPopover.isOpen('search') && (
              <SearchFilterContent
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Invoice no, contract, CP name, status…"
                onClear={() => filterPopover.close()}
              />
            )}
          </FilterPopover>

          {/* Per-column filter popover (1 instance) */}
          <ColumnFilterPopover
            open={colPopover !== null}
            anchorPosition={colPopover?.position ?? null}
            onClose={() => setColPopover(null)}
            label={popoverColumn?.label ?? ''}
            kind={popoverKind === 'none' ? 'text' : popoverKind}
            value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
            onChange={(v) => {
              if (!colPopover) return
              columnFilters.setFilter(String(colPopover.key), v)
            }}
            options={popoverOptions}
          />

          <Box sx={{ flex: 1, minHeight: 180, overflow: 'hidden', position: 'relative' }}>
            <TableVirtuoso
              style={{ height: '100%' }}
              data={displayRows}
              components={{
                Table: (props) => (
                  <Table
                    {...props}
                    size="small"
                    stickyHeader
                    sx={{ minWidth: minTableWidth, width: '100%' }}
                  />
                ),
                TableHead: ((props: object) => <TableHead {...props} component="thead" />) as never,
                TableBody: ((props: object) => <TableBody {...props} component="tbody" />) as never,
                TableRow: (props) => {
                  const idx = Number((props as unknown as Record<string, unknown>)['data-item-index'])
                  const row = Number.isFinite(idx) ? displayRows[idx] : null
                  const checked = row != null && ticked.has(rowKey(row))
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={checked}
                      sx={{ cursor: canEdit ? 'pointer' : 'default' }}
                      onClick={() => row && canEdit && toggleRow(row)}
                    />
                  )
                },
              }}
              fixedHeaderContent={() => (
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    className="app-table-header-cell"
                    style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH }}
                  >
                    <Checkbox
                      size="small"
                      checked={allTicked}
                      indeterminate={someTicked}
                      disabled={!canEdit || displayRows.length === 0}
                      onChange={toggleTickAll}
                    />
                  </TableCell>
                  {TABLE_COLUMNS.map((col) => {
                    const key = String(col.key)
                    const w = getWidth(key) ?? col.width
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'text',
                          position: 'relative',
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={
                displayRows.length > 0
                  ? () => (
                      <TableRow>
                        <TableCell padding="checkbox" sx={footerCellSx} style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH }} />
                        {TABLE_COLUMNS.map((col, i) => {
                          const key = String(col.key)
                          const w = getWidth(key) ?? col.width
                          const isSum = SUM_KEYS.has(col.key)
                          // "Total" ở cột dữ liệu đầu tiên (không neo theo tên cột cụ
                          // thể → luôn hiện dù cột nào bị bỏ). Cột sum hiện tổng.
                          const content = i === 0 ? 'Total' : isSum ? fmtAmount((totals as Record<string, number>)[key]) : ''
                          return (
                            <TableCell
                              key={key}
                              align={i === 0 ? 'left' : col.align}
                              style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                              sx={isSum ? { ...footerCellSx, ...numSx } : footerCellSx}
                            >
                              {content}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    )
                  : undefined
              }
              itemContent={(_, row) => (
                <>
                  <TableCell
                    padding="checkbox"
                    style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      size="small"
                      checked={ticked.has(rowKey(row))}
                      disabled={!canEdit}
                      onChange={() => toggleRow(row)}
                    />
                  </TableCell>
                  {TABLE_COLUMNS.map((col) => {
                    const key = String(col.key)
                    const w = getWidth(key) ?? col.width
                    const val = cellValue(row, col)
                    const wrap = col.wrap || getWidth(key) != null
                    const alignSx = col.align === 'right' ? numSx : undefined
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={[
                          wrap
                            ? { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }
                            : { verticalAlign: 'top' },
                          ...(alignSx ? [alignSx] : []),
                        ]}
                      >
                        <Box
                          component="span"
                          className={wrap ? 'app-table-cell-wrap' : 'app-table-cell-text'}
                          title={wrap ? undefined : val || '-'}
                        >
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  })}
                </>
              )}
            />
            {displayRows.length === 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  top: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  pointerEvents: 'none',
                }}
              >
                {invLoading
                  ? 'Loading…'
                  : !cpCode
                    ? 'Select a Vendor to load invoices.'
                    : 'No invoices for the chosen filter.'}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
